import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { HomeComponent } from "./home/home.component";
import { PageNotFoundComponent } from "./error-routing/not-found/not-found.component";
import { UncaughtErrorComponent } from "./error-routing/error/uncaught-error.component";
import { ErrorRoutingModule } from "./error-routing/error-routing.module";
import { LoginComponent } from "./shared/modules/login/login.component";
import { PasswordRecoveryComponent } from "./shared/modules/password-recovery/password-recovery.component";
import { WelcomeComponent } from "./welcome/welcome.component";

import { AuthGuard } from "./shared";
import { TranslateService } from "@ngx-translate/core";
import { RoleGuard } from "./shared/guard/role.guard";

// canActivate: [AuthGuard]
export let routes: Routes = [
  { path: "", redirectTo: "welcome", pathMatch: "full" },
  {
    path: "welcome",
    component: WelcomeComponent,
  },
  {
    path: "home",
    component: HomeComponent,
    data: {
      code: "home_dashboard",
      roleCode: "HOME_DASHBOARD",
      permissionCode: "READ",
      text: "Home",
      iconName: "fa-home",
      iconFontSet: "fa",
    },
    canActivate: [AuthGuard, RoleGuard],
  },
  { path: "error", component: UncaughtErrorComponent },
  { path: "login", component: LoginComponent, data: { text: "login" } },
  {
    path: "passwordRecovery",
    component: PasswordRecoveryComponent,
    data: { text: "passwordRecovery" },
  },
  {
    path: "customers",
    loadChildren: () =>
      import("./layout-modules/customers-Management/customers.module").then(
        (m) => m.CustomersModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "customer_management",
      roleCode: "CUSTOMER_MANAGEMENT", // role usado pelo RoleGuard
      permissionCode: "READ",         // permissão necessária para entrar na página
      text: "Customers",
      iconName: "fa-users",
      iconFontSet: "fa",
    }
  },
  {
    path: "employees",
    loadChildren: () =>
      import("./layout-modules/employees-Management/employees.module").then(
        (m) => m.EmployeesModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "employee_management",
      roleCode: "EMPLOYEE_MANAGEMENT",
      permissionCode: "READ",
      text: "Employees",
      iconName: "fa-id-badge",
      iconFontSet: "fa",
    },
  },
  {
    path: "suppliers",
    loadChildren: () =>
      import("./layout-modules/suppliers-Management/suppliers.module").then(
        (m) => m.SuppliersModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "supplier_management",
      roleCode: "SUPPLIER_MANAGEMENT",
      permissionCode: "READ",
      text: "Suppliers",
      iconName: "fa-boxes",
      iconFontSet: "fa",
    },
  },
  {
    path: "contracts",
    loadChildren: () =>
      import("./layout-modules/contracts-Management/contracts.module").then(
        (m) => m.ContractsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "contract_management",
      roleCode: "CONTRACT_MANAGEMENT",
      permissionCode: "READ",
      text: "Contracts",
      iconName: "fa-file-alt",
      iconFontSet: "fa",
    },
  },
  {
    path: "vehicles",
    loadChildren: () =>
      import("./layout-modules/vehicles-Management/vehicles.module").then(
        (m) => m.VehiclesModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "vehicle_management",
      roleCode: "VEHICLE_MANAGEMENT",
      permissionCode: "READ",
      text: "Vehicles",
      iconName: "fa-truck",
      iconFontSet: "fa",
    },
  },
  {
    path: "cards",
    loadChildren: () =>
      import("./layout-modules/cards-Management/cards.module").then(
        (m) => m.CardsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "card_management",
      roleCode: "CARD_MANAGEMENT",
      permissionCode: "READ",
      text: "Cards",
      iconName: "fa-id-card",
      iconFontSet: "fa",
    },
  },
  {
    path: "properties",
    loadChildren: () =>
      import("./layout-modules/properties-Management/property.module").then(
        (m) => m.PropertiesModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "property_management",
      roleCode: "PROPERTY_MANAGEMENT",
      permissionCode: "READ",
      text: "Properties",
      iconName: "fa-clipboard-list",
      iconFontSet: "fa",
    },
  },
  {
    path: "prompts",
    loadChildren: () =>
      import("./layout-modules/prompt-Management/prompt.module").then(
        (m) => m.PromptModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "card_prompt_management",
      roleCode: "CARD_PROMPT_MANAGEMENT",
      permissionCode: "READ",
      text: "Card Prompts",
      iconName: "fa-terminal",
      iconFontSet: "fa",
    },
  },
  {
    path: "categories",
    loadChildren: () =>
      import("./layout-modules/categories-Management/category.module").then(
        (m) => m.CategoryModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "category_management",
      roleCode: "CATEGORY_MANAGEMENT",
      permissionCode: "READ",
      text: "Categories",
      iconName: "fa-folder-open",
      iconFontSet: "fa",
    },
  },
  {
    path: "users",
    loadChildren: () =>
      import("./layout-modules/users-Management/users.module").then(
        (m) => m.UsersModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "user_management",
      roleCode: "USER_MANAGEMENT",
      permissionCode: "READ",
      text: "Users",
      iconName: "fa-user",
      iconFontSet: "fa",
    },
  },
  {
    path: "reports-generation",
    loadChildren: () =>
      import(
        "./layout-modules/reports-generation-Management/reports-generation.module"
      ).then((m) => m.ReportsGenerationModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "report_generation",
      roleCode: "REPORT_GENERATION",
      permissionCode: "READ",
      text: "Reports Generation",
      iconName: "fa-file-signature",
      iconFontSet: "fa",
    },
  },
  {
    path: "reports",
    loadChildren: () =>
      import("./layout-modules/reports-Management/reports.module").then(
        (m) => m.ReportsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "report_history",
      roleCode: "REPORT_HISTORY",
      permissionCode: "READ",
      text: "Reports",
      iconName: "fa-clipboard-list",
      iconFontSet: "fa",
    },
  },
  {
    path: "movements",
    loadChildren: () =>
      import("./layout-modules/movements-Management/movements.module").then(
        (m) => m.MovementsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "contract_movements",
      roleCode: "CONTRACT_MOVEMENTS",
      permissionCode: "READ",
      text: "Movements",
      iconName: "fa-chart-line",
      iconFontSet: "fa",
    },
  },
  {
    path: "movements-customer-headers",
    loadChildren: () =>
      import("./layout-modules/movements2-customer-Management/movements2-customer.module").then(
        (m) => m.Movements2CustomerModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "document_management",
      roleCode: "DOCUMENT_MANAGEMENT",
      permissionCode: "READ",
      text: "Transactions Customer",
      iconName: "fa-list",
      iconFontSet: "fa",
    },
  },
  {
    path: "movements-supplier-headers",
    loadChildren: () =>
      import("./layout-modules/movements2-supplier-Management/movements2-supplier.module").then(
        (m) => m.Movements2SupplierModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "purchase_document_management",
      roleCode: "PURCHASE_DOCUMENT_MANAGEMENT",
      permissionCode: "READ",
      text: "Transactions Supplier",
      iconName: "fa-list",
      iconFontSet: "fa",
    },
  },
  {
    path: "movements-internal-headers",
    loadChildren: () =>
      import("./layout-modules/movements2-internal-Management/movements2-internal.module").then(
        (m) => m.Movements2InternalModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "internal_document_management",
      roleCode: "INTERNAL_DOCUMENT_MANAGEMENT",
      permissionCode: "READ",
      text: "Transactions Internal",
      iconName: "fa-list",
      iconFontSet: "fa",
    },
  },
  {
    path: "sale-details",
    loadChildren: () =>
      import("./layout-modules/movements3-Management/movements3.module").then(
        (m) => m.Movements3Module
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "sale_details",
      roleCode: "SALE_DETAILS",
      permissionCode: "READ",
    },
  },
  {
    path: "professionalFuel",
    loadChildren: () =>
      import(
        "./layout-modules/professional-fuel-Management/professional-fuel.module"
      ).then((m) => m.ProfessionalFuelModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "professional_fuel_pt",
      roleCode: "PROFESSIONAL_FUEL_PT",
      permissionCode: "READ",
      text: "Professional Fuel",
      iconName: "fa-chart-line",
      iconFontSet: "fa",
    },
  },
  {
    path: "professionalFuelEs",
    loadChildren: () =>
      import(
        "./layout-modules/professional-fuel-es-Management/professional-fuel-es.module"
      ).then((m) => m.ProfessionalFuelEsModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "professional_fuel_es",
      roleCode: "PROFESSIONAL_FUEL_ES",
      permissionCode: "READ",
      text: "Professional Fuel Es",
      iconName: "fa-chart-line",
      iconFontSet: "fa",
    },
  },
  {
    path: "current-accounts",
    loadChildren: () =>
      import(
        "./layout-modules/current-accounts-Management/current-accounts.module"
      ).then((m) => m.CurrentAccountsModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "current_accounts_management",
      roleCode: "CURRENT_ACCOUNTS_MANAGEMENT",
      permissionCode: "READ",
      text: "Current Accounts",
      iconName: "fa-address-book",
      iconFontSet: "fa",
    },
  },
  {
    path: "deliveries",
    loadChildren: () =>
      import("./layout-modules/delivery-Management/deliveries.module").then(
        (m) => m.DeliveryModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "stock_deliveries",
      roleCode: "STOCK_DELIVERIES",
      permissionCode: "READ",
      text: "FuelDeliveries",
      iconName: "fa-chart-line",
      iconFontSet: "fa",
    },
  },
  {
    path: "fuelStocks",
    loadChildren: () =>
      import("./layout-modules/fuel-stock-Management/fuel-stocks.module").then(
        (m) => m.FuelStocksModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "stock_management",
      roleCode: "STOCK_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-tachometer-alt",
      iconName: "fa-chart-line",
      iconFontSet: "fa",
    },
  },
  {
    path: "sites",
    loadChildren: () =>
      import("./layout-modules/sites-Management/sites.module").then(
        (m) => m.SitesModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "site_management",
      roleCode: "SITE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-building",
      iconName: "fa-building",
      iconFontSet: "fa",
    },
  },
  {
    path: "products",
    loadChildren: () =>
      import("./layout-modules/products-Management/product.module").then(
        (m) => m.ProductModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "product_management",
      roleCode: "PRODUCT_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-building",
      iconName: "fa-building",
      iconFontSet: "fa",
    },
  },
  {
    path: "families",
    loadChildren: () =>
      import(
        "./layout-modules/product-family-Management/product-family.module"
      ).then((m) => m.ProductFamilyModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "product_family_management",
      roleCode: "PRODUCT_FAMILY_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-building",
      iconName: "fa-building",
      iconFontSet: "fa",
    },
  },
  {
    path: "groups",
    loadChildren: () =>
      import(
        "./layout-modules/product-group-Management/product-group.module"
      ).then((m) => m.ProductGroupModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "product_group_management",
      roleCode: "PRODUCT_GROUP_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "price-scheduling",
    loadChildren: () =>
      import(
        "./layout-modules/product-price-scheduling-Management/product-price-scheduling.module"
      ).then((m) => m.ProductPriceSchedulingModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "site_product_price_scheduler",
      roleCode: "SITE_PRODUCT_PRICE_SCHEDULER",
      permissionCode: "READ",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "grades",
    loadChildren: () =>
      import("./layout-modules/grades-Management/grades.module").then(
        (m) => m.GradesModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "grade_management",
      roleCode: "GRADE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "tenants",
    loadChildren: () =>
      import("./layout-modules/tenant-Management/tenant-config.module").then(
        (m) => m.TenantConfigModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "tenant_management",
      roleCode: "TENANT_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "tenant-current",
    loadChildren: () =>
      import("./layout-modules/tenant-current-Management/tenant-current.module").then(
        (m) => m.TenantCurrentModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "tenant_current_management",
      roleCode: "TENANT_CURRENT_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-layer-group",
      iconName: "fa-gear",
      iconFontSet: "fa",
    },
  },
  {
    path: "pos-cards",
    loadChildren: () =>
      import("./layout-modules/pos-Management/pos-card.module").then(
        (m) => m.PosCardModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "card_actions",
      roleCode: "CARD_ACTIONS",
      permissionCode: "READ",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  // {
  //   path: "dictionary",
  //   loadChildren: () =>
  //     import("./layout-modules/dictionary-Management/dictionary.module").then(
  //       (m) => m.DictionaryModule
  //     ),
  //   canActivate: [AuthGuard, RoleGuard],
  //   data: {
  //     code: "dictionary_management",
  //     roleCode: "DICTIONARY_MANAGEMENT",
  //     permissionCode: "READ",
  //     text: "fa-layer-group",
  //     iconName: "fa-layer-group",
  //     iconFontSet: "fa",
  //   },
  // },
  {
    path: "service-modes",
    loadChildren: () =>
      import(
        "./layout-modules/service-modes-Management/service-modes.module"
      ).then((m) => m.ServiceModeModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "service_mode_management",
      roleCode: "SERVICE_MODE_MANAGEMENT",
      permissionCode: "READ",
    },
  },
  {
    path: "fueling-modes",
    loadChildren: () =>
      import(
        "./layout-modules/fueling-mode-Management/fueling-mode.module"
      ).then((m) => m.FuelingModeModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "fuelling_mode_management",
      roleCode: "FUELLING_MODE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "locales",
    loadChildren: () =>
      import("./layout-modules/locales-Management/locales.module").then(
        (m) => m.LocaleModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "locale_management",
      roleCode: "LOCALE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "multimedia-guides",
    loadChildren: () =>
      import("./layout-modules/guides-Management/guide.module").then(
        (m) => m.GuideModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "multimedia_guide_management",
      roleCode: "MULTIMEDIA_GUIDE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-calendar-week",
      iconName: "fa-calendar-week",
      iconFontSet: "fa",
    },
  },
  {
    path: "multimedia-sequences",
    loadChildren: () =>
      import("./layout-modules/sequences-Management/sequence.module").then(
        (m) => m.SequenceModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "multimedia_sequence_management",
      roleCode: "MULTIMEDIA_SEQUENCE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-file-video",
      iconName: "fa-file-video",
      iconFontSet: "fa",
    },
  },
  {
    path: "multimedia-playlists",
    loadChildren: () =>
      import("./layout-modules/playlists-Management/playlist.module").then(
        (m) => m.PlaylistModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "multimedia_playlist_management",
      roleCode: "MULTIMEDIA_PLAYLIST_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-file-video",
      iconName: "fa-file-video",
      iconFontSet: "fa",
    },
  },
  {
    path: "multimedia-terminals",
    loadChildren: () =>
      import("./layout-modules/terminals-Management/terminals.module").then(
        (m) => m.TerminalsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "multimedia_device_management",
      roleCode: "MULTIMEDIA_DEVICE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-tv",
      iconName: "fa-tv",
      iconFontSet: "fa",
    },
  },
  {
    path: "taxes",
    loadChildren: () =>
      import("./layout-modules/taxes-Management/taxes.module").then(
        (m) => m.TaxesModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "tax_management",
      roleCode: "TAX_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-percent",
      iconName: "fa-percent",
      iconFontSet: "fa",
    },
  },
  {
    path: "regions",
    loadChildren: () =>
      import("./layout-modules/regions-Management/regions.module").then(
        (m) => m.RegionsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "region_management",
      roleCode: "REGION_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-map",
      iconName: "fa-map",
      iconFontSet: "fa",
    },
  },
  {
    path: "units",
    loadChildren: () =>
      import("./layout-modules/units-Management/units.module").then(
        (m) => m.UnitsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "product_unit_management",
      roleCode: "PRODUCT_UNIT_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-balance-scale",
      iconName: "fa-balance-scale",
      iconFontSet: "fa",
    },
  },
  {
    path: "payment-modes",
    loadChildren: () =>
      import("./layout-modules/payment-modes-Management/payment-modes.module").then(
        (m) => m.PaymentModesModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "payment_modes_management",
      roleCode: "PAYMENT_MODES_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-creadit-card",
      iconName: "fa-creadit-card",
      iconFontSet: "fa",
    },
  },
  {
    path: "receipts",
    loadChildren: () =>
      import("./layout-modules/receipts-Management/receipts.module").then(
        (m) => m.ReceiptsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "receipt_management",
      roleCode: "RECEIPT_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "entities",
    loadChildren: () =>
      import("./layout-modules/entity-Management/entity.module").then(
        (m) => m.EntityModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "entity_management",
      roleCode: "ENTITY_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "entity-current",
    loadChildren: () =>
      import("./layout-modules/entity-current-Management/entity-current.module").then(
        (m) => m.EntityCurrentModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "entity_current_management",
      roleCode: "ENTITY_CURRENT_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-layer-group",
      iconName: "fa-gear",
      iconFontSet: "fa",
    },
  },
  {
    path: "manual-transaction",
    loadChildren: () =>
      import("./layout-modules/manual-transaction-management/manual-transaction.module").then(
        (m) => m.ManualTransactionModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "fleet_manual_transaction",
      roleCode: "FLEET_MANUAL_TRANSACTION",
      permissionCode: "READ",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "devices",
    loadChildren: () =>
    import("./layout-modules/devices-Management/devices.module").then(
      (m) => m.DevicesModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "device_management",
      roleCode: "DEVICE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "document-types",
    loadChildren: () =>
    import("./layout-modules/document-types-Management/document-types.module").then(
      (m) => m.DcumentTypesModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "document_type",
      roleCode: "DOCUMENT_TYPE",
      permissionCode: "READ",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "series",
    loadChildren: () =>
    import("./layout-modules/series-Management/series.module").then(
      (m) => m.SeriesModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "series",
      roleCode: "SERIES",
      permissionCode: "READ",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "site-groups",
    loadChildren: () =>
    import("./layout-modules/site-groups-Management/site-groups.module").then(
      (m) => m.SiteGroupsModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "site_group_management",
      roleCode: "SITE_GROUP_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "shifts",
    loadChildren: () =>
    import("./layout-modules/sites-Management/shifts/shifts.module").then(
      (m) => m.ShiftsModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "shift_management",
      roleCode: "SHIFT_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-user-clock",
      iconName: "fa-user-clock",
      iconFontSet: "fa",
    },
  },
  {
    path: "shift-types",
    loadChildren: () =>
    import("./layout-modules/sites-Management/shift-types/shift-types.module").then(
      (m) => m.ShiftTypesModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "shift_type_management",
      roleCode: "SHIFT_TYPE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-user-cog",
      iconName: "fa-user-cog",
      iconFontSet: "fa",
    },
  },
  {
    path: "shift-manual-movements",
    loadChildren: () =>
    import("./layout-modules/sites-Management/shift-manual-movements/shift-manual-movements.module").then(
      (m) => m.ShiftManualMovementsModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "shift_manual_movements_management",
      roleCode: "SHIFT_MANUAL_MOVEMENTS_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-hand-holding-usd",
      iconName: "fa-hand-holding-usd",
      iconFontSet: "fa",
    },
  },
  {
    path: "shift-manual-movement-types",
    loadChildren: () =>
    import("./layout-modules/sites-Management/shift-manual-movement-types/shift-manual-movement-types.module").then(
      (m) => m.ShiftManualMovementTypesModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "shift_manual_movements_type_management",
      roleCode: "SHIFT_MANUAL_MOVEMENTS_TYPE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-hand-holding-usd",
      iconName: "fa-hand-holding-usd",
      iconFontSet: "fa",
    },
  },
  {
    path: "customer-billing",
    loadChildren: () =>
    import("./layout-modules/customer-multi-billing/customer-multi-billing.module").then(
      (m) => m.CustomerMultiBillingModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "customer_billing",
      roleCode: "CUSTOMER_BILLING",
      permissionCode: "READ",
      text: "fa-dollar-sign",
      iconName: "fa-dollar-sign",
      iconFontSet: "fa",
    },
  },
  {
    path: "saft",
    loadChildren: () =>
    import("./layout-modules/saft-Management/saft.module").then(
      (m) => m.SaftModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "saft",
      roleCode: "SAFT",
      permissionCode: "READ",
      text: "fa-book",
      iconName: "fa-book",
      iconFontSet: "fa",
    },
  },
  {
    path: "loyalties",
    loadChildren: () =>
    import("./layout-modules/loyalty-Management/loyalty.module").then(
      (m) => m.LoyaltyModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "loyalty_provider_management",
      roleCode: "LOYALTY_PROVIDER_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-ticket",
      iconName: "fa-ticket",
      iconFontSet: "fa",
    },
  },
  {
    path: "profile-management",
    loadChildren: () =>
      import("./layout-modules/user-profile-Managment/user-profile.module").then(
        (m) => m.UserProfileModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "profile_management",
      roleCode: "PROFILE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-book",
      iconName: "fa-book",
      iconFontSet: "fa",
    },
  },
  {
    path: "importers",
    loadChildren: () =>
      import("./layout-modules/importer-Management/importer.module").then(
        (m) => m.ImporterModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "importer",
      roleCode: "IMPORTER",
      permissionCode: "READ",
      text: "fa-book",
      iconName: "fa-book",
      iconFontSet: "fa",
    },
  },
  {
    path: "jobs",
    loadChildren: () =>
      import("./layout-modules/jobs-Management/jobs.module").then(
        (m) => m.JobsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "job_management",
      roleCode: "JOB_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-calendar-check",
      iconName: "fa-calendar-check",
      iconFontSet: "fa",
    },
  },
  {
    path: "integration-providers",
    loadChildren: () =>
      import("./layout-modules/integration-providers-Management/integration-providers.module").then(
        (m) => m.IntegrationProvidersModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "integration_provider_management",
      roleCode: "INTEGRATION_PROVIDER_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-calendar-check",
      iconName: "fa-calendar-check",
      iconFontSet: "fa",
    },
  },
  {
    path: "wetstock",
    loadChildren: () =>
      import("./layout-modules/wetstock-Management/wetstock.module").then(
        (m) => m.WetstockModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "wet_stock_management",
      roleCode: "WET_STOCK_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-percent",
      iconName: "fa-percent",
      iconFontSet: "fa",
    },
  },
  {
    path: "multimedia-fallback-video",
    loadChildren: () =>
      import("./layout-modules/fallback-video-Management/fallback-video.module").then(
        (m) => m.FallbackVideoModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "multimedia_fallback_video_management",
      roleCode: "MULTIMEDIA_FALLBACK_VIDEO_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-file-video",
      iconName: "fa-file-video",
      iconFontSet: "fa",
    },
  },
  {
    path: "entity-current-manager",
    data: {
      code: "entity_current_management",
    },
    component: PageNotFoundComponent
  },
  {
    path: "multimedia-mass-assignments",
    loadChildren: () =>
      import("./layout-modules/mass-assignments-Management/mass-assignments.module").then(
        (m) => m.MassAssignmentsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "multimedia_mass_assignments",
      roleCode: "MULTIMEDIA_MASS_ASSIGNMENTS",
      permissionCode: "READ",
      text: "fa-tv",
      iconName: "fa-tv",
      iconFontSet: "fa",
    },
  },
  {
    path: "electronic-invoices",
    loadChildren: () =>
    import("./layout-modules/electronic-invoice-Management/electronic-invoice.module").then(
      (m) => m.ElectronicInvoiceModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "fe_management",
      roleCode: "FE_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "electronic-invoices-configuration-management",
    loadChildren: () =>
    import("./layout-modules/electronic-invoices-config-management/eletronic-invoices-config-management.module").then(
      (m) => m.ElectronicInvoicesConfigManagementModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "fe_config_management",
      roleCode: "FE_CONFIG_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "series-agt",
    loadChildren: () =>
    import("./layout-modules/series-agt-Management/series-agt.module").then(
      (m) => m.SeriesAgtModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "series_agt",
      roleCode: "SERIES_AGT",
      permissionCode: "READ",
      text: "fa-book",
      iconName: "fa-book",
      iconFontSet: "fa",
    },
  },
  {
    path: "tax-scheduler",
    loadChildren: () =>
    import("./layout-modules/tax-scheduler-management/tax-scheduler-management.module").then(
      (m) => m.TaxSchedulerManagementModule
    ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      code: "tax_scheduler_management",
      roleCode: "TAX_SCHEDULER_MANAGEMENT",
      permissionCode: "READ",
      text: "fa-book",
      iconName: "fa-caledar-check",
      iconFontSet: "fa",
    },
  },
  { path: "**", component: PageNotFoundComponent }, // must always be last
];
// directions_bus
@NgModule({
  imports: [
    RouterModule.forRoot(routes, { relativeLinkResolution: "legacy" }),
    ErrorRoutingModule,
  ],
  exports: [RouterModule, ErrorRoutingModule],
})
export class AppRoutingModule {
  constructor(private translate: TranslateService) {}
}
